import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table'
import { HTMLAttributes, ReactNode, useContext, useMemo, useState } from 'react'

import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Link } from 'react-router-dom'
import { Trans } from 'react-i18next'
import AddTeamMemberModal from '../../TeamRoster/components/AddTeamMemberModal'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import { sum } from 'lodash'
import TeamWishTooltip from './TeamWishTooltip'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'
import { LootContext } from '../context'
import WishLegacyPriorityDot from 'app/frontend/base/components/WishLegacyPriorityDot'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import { crestTypes } from 'app/frontend/types/custom/CrestType'
import { colorByTrack } from 'app/frontend/types/custom/UpgradeTrack'
import { crestEarnableCap } from 'app/frontend/base/helpers/crestCap'
import {
  formatUnixAge,
  formatUnixTimestamp,
  isUnixAtLeastDaysOld,
} from 'app/frontend/base/helpers/relativeAge'
import { Clock, Dices, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'

type Props = {
  teamWishlist: wowaudit.TeamWishlist
}

type EncounterItemWithWishes = Omit<TeamWishlistItem, 'wishlists'> &
  TeamWishlistItemCharacterWishlist

type CharacterEncounterWishOverview = wowaudit.CharacterReference & {
  encounters: {
    id: number | null
    items: EncounterItemWithWishes[]
  }[]
  totalScore: number
}

const TableShell = ({
  className,
  children,
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={className}>{children}</div>
)

const leftoverCrestTypes = crestTypes.filter((crest) => crest !== 'Veteran')

const leftoverAmount = (value: unknown) => {
  if (value == null || value === '') return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

const missingCurrencyTooltip =
  "This character hasn't recently uploaded a Raidbots report based on a SimC export, and does not have the RCLootCouncil_wowaudit module installed."

const MissingCurrencyCell = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className='text-muted-foreground opacity-50'>?</span>
    </TooltipTrigger>
    <TooltipContent className='max-w-80 text-center'>
      {missingCurrencyTooltip}
    </TooltipContent>
  </Tooltip>
)

const AuditCell = ({ children }: { children: ReactNode }) => (
  <div className='audit-currency-cell'>{children}</div>
)

const encounterColumnSize = (maxIcons: number) => {
  const iconRow = maxIcons * 24 + Math.max(maxIcons - 1, 0) * 4
  return Math.max(112, iconRow + 32)
}

const TeamWishlistTable = ({ teamWishlist }: Props) => {
  const { team, guild } = useTeam()
  const { accessLevel } = useAuth()
  const [addingMembers, setAddingMembers] = useState(false)
  const { valueDisplay, selectedSeason } = useContext(LootContext)
  const {
    data: { currentPeriod, currentSeason },
  } = usePageInfo()
  const { data: auditData, isPending: isAuditPending } = useTeamAuditData(
    team.id,
  )
  const period = currentPeriod[guild?.realm.region ?? 'EU']

  const bonusRollCountByEncounter = useMemo(() => {
    const counts: Record<number, number> = {}
    if (!selectedSeason.bonus_rolls_available) return counts

    const visibleMembers = (team.members ?? []).filter(
      (member) => member.teamRank?.wishlist_visibility,
    )
    visibleMembers.forEach((member) => {
      member.characterReference.bonusRollEncounterIds.forEach((encounterId) => {
        counts[encounterId] = (counts[encounterId] ?? 0) + 1
      })
    })

    return counts
  }, [team.members, selectedSeason.bonus_rolls_available])

  const tableData = useMemo((): CharacterEncounterWishOverview[] => {
    return (team.members ?? [])
      ?.filter((member) => member.teamRank?.wishlist_visibility)
      .map((member) => {
        const encounters = teamWishlist.encounters.map((encounter) => {
          const items = encounter.items.reduce((acc, item) => {
            const characterWishlist = item.wishlists.find(
              (wishlist) =>
                wishlist.character_id === member.characterReference.id,
            )

            if (characterWishlist) {
              acc.push({ ...item, ...characterWishlist })
            }
            return acc
          }, [] as EncounterItemWithWishes[])

          return {
            id: encounter.id,
            items,
          }
        })

        const totalScore = sum(
          encounters.flatMap((encounter) =>
            encounter.items.map((item) => {
              const property =
                valueDisplay === 'percentage' ? 'percentage' : 'score'

              if (!isNaN(item.score_by_spec.total[property] as number)) {
                return item.score_by_spec.total[property]
              } else return 0
            }),
          ),
        )

        return {
          ...member.characterReference,
          encounters,
          totalScore,
        }
      })
  }, [team.members, teamWishlist.encounters, valueDisplay])

  const columns: ColumnDef<CharacterEncounterWishOverview>[] = useMemo(
    () => [
      {
        accessorKey: 'totalScore',
        id: 'totalScore',
        size: 200,
        header: ({ table, column }) => (
          <div className='flex items-center gap-1'>
            <span
              className='cursor-pointer'
              onClick={() => {
                const currentSorting = table.getState().sorting
                const nameSort = currentSorting.find(
                  (sort) => sort.id === 'name',
                )
                const isCurrentlyAsc = !nameSort?.desc
                table.setSorting([
                  { id: 'name', desc: nameSort ? isCurrentlyAsc : false },
                ])
              }}
            >
              Name
            </span>
            <span>/</span>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              score
            </span>
          </div>
        ),
        sortingFn: 'alphanumeric',
        cell: (info) => {
          const syncedAt =
            auditData?.[info.row.original.id]?.last_refresh?.crests_updated_at

          return (
            <div className='flex flex-col gap-1'>
              <TeamMemberLink
                characterReference={info.row.original}
                iconType='class'
              />
              <div className='flex items-center gap-1.5'>
                <div className='text-success-foreground text-sm'>
                  {info.row.original.totalScore > 0 ? (
                    <span>
                      {info.row.original.totalScore.toLocaleString('en-US', {
                        maximumFractionDigits:
                          valueDisplay === 'percentage' ? 2 : 0,
                        minimumFractionDigits:
                          valueDisplay === 'percentage' ? 2 : 0,
                      })}
                      {valueDisplay === 'percentage' ? '%' : null}
                    </span>
                  ) : (
                    ''
                  )}
                </div>
                <AuditCell>
                  {syncedAt != null && syncedAt !== '' ? (
                    <Tooltip>
                      <TooltipTrigger className='inline-flex'>
                        <Badge
                          variant='outline'
                          className={cn(
                            'px-1.5 py-0 font-normal text-sm gap-1 whitespace-nowrap',
                            isUnixAtLeastDaysOld(syncedAt, 7) &&
                              'text-warning-foreground',
                          )}
                        >
                          <Clock className='w-3 h-3' />
                          {formatUnixAge(syncedAt)}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        Crest and catalyst data synced at{' '}
                        {formatUnixTimestamp(syncedAt)}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </AuditCell>
              </div>
            </div>
          )
        },
      },
      {
        // For sorting only, not visible in the table
        id: 'name',
        accessorKey: 'name',
      },
      {
        id: 'crests_left',
        header: () => (
          <span className='inline-flex items-center justify-center gap-1'>
            Unspent Crests
            <Tooltip>
              <TooltipTrigger
                asChild
                onClick={(event) => event.stopPropagation()}
              >
                <Info className='w-3 h-3 text-muted-foreground' />
              </TooltipTrigger>
              <TooltipContent className='max-w-80 text-center'>
                Pulled from the SimC export used to run the Raidbots report, or
                synced from the game with the RCLootCouncil_wowaudit module.
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        meta: { className: 'border-l text-center' },
        columns: leftoverCrestTypes.map((crest, index) => ({
          id: `${crest}_crests_left`,
          size: 100,
          meta: {
            className: cn(index === 0 && 'border-l', 'text-center'),
          },
          accessorFn: (row: CharacterEncounterWishOverview) =>
            leftoverAmount(
              auditData?.[row.id]?.last_refresh?.[
                `${crest.toLowerCase()}_crests_left`
              ],
            ),
          ...genericSorting('basic', crest),
          cell: (
            info: CellContext<CharacterEncounterWishOverview, unknown>,
          ) => {
            const lastRefresh = auditData?.[info.row.original.id]?.last_refresh
            const left = lastRefresh?.[`${crest.toLowerCase()}_crests_left`]
            if (left == null || left === '') {
              return (
                <AuditCell>
                  {isAuditPending ? null : <MissingCurrencyCell />}
                </AuditCell>
              )
            }

            const earned = Number(
              lastRefresh?.[`${crest.toLowerCase()}_crests`],
            )
            const seasonCrest = currentSeason.crests.find(
              (candidate) => candidate.name === crest,
            )
            const remainingToEarn =
              seasonCrest &&
              !currentSeason.crests_uncapped &&
              Number.isFinite(earned)
                ? Math.max(0, crestEarnableCap(seasonCrest, period) - earned)
                : null

            return (
              <AuditCell>
                <div className='flex flex-col items-center gap-1'>
                  <span className={colorByTrack[crest]}>{left}</span>
                  {remainingToEarn != null && remainingToEarn > 0 ? (
                    <Badge
                      variant='outline'
                      className='px-1.5 py-0 font-normal text-sm whitespace-nowrap'
                    >
                      +{remainingToEarn} to earn
                    </Badge>
                  ) : remainingToEarn === 0 ? (
                    <Badge
                      variant='success'
                      className='px-1.5 py-0 font-normal text-sm whitespace-nowrap'
                    >
                      cap reached
                    </Badge>
                  ) : null}
                </div>
              </AuditCell>
            )
          },
        })),
      },
      {
        id: 'catalyst',
        header: ({
          table,
        }: HeaderContext<CharacterEncounterWishOverview, unknown>) => (
          <span
            className='cursor-pointer'
            onClick={() =>
              table.getColumn('catalyst_charges_left')?.toggleSorting()
            }
          >
            Catalyst
          </span>
        ),
        meta: { className: 'border-l text-center align-bottom' },
        columns: [
          {
            id: 'catalyst_charges_left',
            size: 72,
            meta: { className: 'border-l text-center' },
            accessorFn: (row: CharacterEncounterWishOverview) =>
              leftoverAmount(
                auditData?.[row.id]?.last_refresh?.catalyst_charges_left,
              ),
            ...genericSorting('basic', 'Charges'),
            cell: (
              info: CellContext<CharacterEncounterWishOverview, unknown>,
            ) => {
              const value =
                auditData?.[info.row.original.id]?.last_refresh
                  ?.catalyst_charges_left
              return (
                <AuditCell>
                  {isAuditPending ? null : value == null || value === '' ? (
                    <MissingCurrencyCell />
                  ) : (
                    value
                  )}
                </AuditCell>
              )
            },
          },
        ],
      },
      ...teamWishlist.encounters.map((encounter) => {
        const maxIcons = tableData.reduce((max, row) => {
          const count =
            row.encounters.find((entry) => entry.id === encounter.id)?.items
              .length ?? 0
          return Math.max(max, count)
        }, 0)
        const size = encounterColumnSize(maxIcons)
        const totalValue =
          (valueDisplay === 'percentage'
            ? encounter.total_percentage_value
            : encounter.total_absolute_value) ?? 0
        const bonusRollCount =
          encounter.id != null
            ? bonusRollCountByEncounter[encounter.id] ?? 0
            : 0

        return {
          id: `encounter-group-${encounter.id}`,
          header: ({
            table,
          }: HeaderContext<CharacterEncounterWishOverview, unknown>) => (
            <div
              className='flex items-end justify-center gap-1.5 cursor-pointer'
              onClick={() =>
                table.getColumn(`encounters.${encounter.id}`)?.toggleSorting()
              }
            >
              <img
                src={encounter.thumbnail_url}
                className='w-8 h-8 min-w-8 min-h-8 rounded-full'
              />
              <div className='flex flex-col items-start leading-none'>
                {totalValue > 0 ? (
                  <span className='text-success-foreground text-sm leading-none'>
                    {valueDisplay === 'percentage'
                      ? `${totalValue.toLocaleString('en-US', {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}%`
                      : totalValue.toLocaleString('en-US', {
                          maximumFractionDigits: 0,
                        })}
                  </span>
                ) : encounter.highest_manual_upgrade ? (
                  <span
                    className={`priority ${encounter.highest_manual_upgrade.toLowerCase()}`}
                  />
                ) : (
                  <span className='text-muted-foreground text-sm leading-none'>
                    nothing
                  </span>
                )}
                {selectedSeason.bonus_rolls_available ? (
                  <span
                    className={cn(
                      'flex items-center gap-0.5 mt-0.5 text-sm leading-none text-muted-foreground',
                      encounter.id == null && 'invisible',
                    )}
                  >
                    <Dices
                      size={14}
                      className={
                        bonusRollCount > 0
                          ? 'text-info-foreground'
                          : 'text-muted-foreground'
                      }
                    />
                    {bonusRollCount}
                  </span>
                ) : null}
              </div>
            </div>
          ),
          meta: { className: 'border-l text-center align-bottom pb-0.5' },
          columns: [
            {
              id: `encounters.${encounter.id}`,
              header: ({
                column,
              }: HeaderContext<CharacterEncounterWishOverview, unknown>) => (
                <div
                  className='truncate cursor-pointer'
                  onClick={() => column.toggleSorting()}
                  title={encounter.name}
                >
                  {encounter.name}
                </div>
              ),
              meta: { className: 'border-l text-center' },
              size,
              accessorFn: (row: CharacterEncounterWishOverview) =>
                (
                  row.encounters.find((e) => e.id === encounter.id)?.items ?? []
                ).map((item) =>
                  valueDisplay === 'percentage'
                    ? item.score_by_spec.total.percentage
                    : item.score_by_spec.total.score,
                ),
              cell: (
                info: CellContext<CharacterEncounterWishOverview, unknown>,
              ) => {
                const characterEncounter = info.row.original.encounters.find(
                  (e) => e.id === encounter.id,
                )

                const items =
                  info.row.original.encounters.find(
                    (e) => e.id === encounter.id,
                  )?.items ?? []

                const isBonusRollTarget =
                  encounter.id != null &&
                  info.row.original.bonusRollEncounterIds.includes(encounter.id)

                const bonusRollIcon = isBonusRollTarget ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex'>
                        <Dices size={14} className='text-info-foreground' />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Bonus roll target</TooltipContent>
                  </Tooltip>
                ) : null

                const priorityColor = items.some((item) => item.edited)
                  ? 'text-warning-foreground'
                  : items.some((item) => item.outdated)
                  ? 'text-destructive-foreground'
                  : 'text-success-foreground'

                if (!characterEncounter?.items.length) {
                  return (
                    <div className='flex items-center justify-center gap-1'>
                      <div>-</div>
                      {bonusRollIcon}
                    </div>
                  )
                }

                const sumValue = sum(
                  (info.getValue() as number[]).filter(
                    (value) => !isNaN(value),
                  ),
                )

                const manualUpgrades = (info.getValue() as string[]).filter(
                  (value) => typeof value === 'string',
                )

                const bisCount = items.filter(
                  (item) =>
                    !item.alternative &&
                    !item.wishes.some((wish) => wish.manually_edited),
                ).length

                return (
                  <div className='flex flex-col gap-1'>
                    <div className={`${priorityColor} flex items-center gap-1`}>
                      <span>
                        {sumValue?.toLocaleString('en-US', {
                          maximumFractionDigits:
                            valueDisplay === 'percentage' ? 2 : 0,
                          minimumFractionDigits:
                            valueDisplay === 'percentage' ? 2 : 0,
                        })}
                        {valueDisplay === 'percentage' && '%'}
                      </span>
                      {manualUpgrades.map((upgrade) => (
                        <WishLegacyPriorityDot
                          key={upgrade}
                          priority={(upgrade as WishLegacyPriority) ?? 'none'}
                        />
                      ))}
                      {bisCount > 0 || bonusRollIcon ? (
                        <span className='ml-auto flex items-center gap-1.5'>
                          {bonusRollIcon}
                          {bisCount > 0 ? (
                            <Badge
                              variant='outline'
                              className='px-1.5 py-0 font-normal text-sm whitespace-nowrap'
                            >
                              {bisCount} BiS
                            </Badge>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                    <div className='flex items-center gap-1 flex-nowrap'>
                      {characterEncounter.items.map((item) => {
                        const iconBorder = item.outdated
                          ? 'border-destructive-foreground'
                          : item.alternative || item.edited
                          ? 'border-warning-foreground'
                          : 'border-success-foreground'

                        return (
                          <TeamWishTooltip
                            key={item.id}
                            wishlist={item}
                            itemInfo={{ ...item, wishlists: [] }}
                          >
                            <ItemIcon
                              id={item.id}
                              className={cn(
                                'w-6 h-6 cursor-pointer overflow-hidden rounded-md border',
                                iconBorder,
                              )}
                            />
                          </TeamWishTooltip>
                        )
                      })}
                    </div>
                  </div>
                )
              },
            },
          ],
        }
      }),
    ],
    [
      tableData,
      teamWishlist.encounters,
      valueDisplay,
      auditData,
      currentSeason,
      period,
      bonusRollCountByEncounter,
      selectedSeason.bonus_rolls_available,
      isAuditPending,
    ],
  )

  return (
    <>
      <div
        className={cn(
          '[&_.audit-currency-cell]:transition-opacity [&_.audit-currency-cell]:duration-300',
          isAuditPending && '[&_.audit-currency-cell]:opacity-0',
        )}
      >
        <DataTable
          columns={columns}
          cellClassName='align-top'
          data={tableData}
          includeHeader={true}
          stickyFirstColumn
          wrapperComponent={TableShell}
          headerClassName={(i) => (i === 0 ? 'text-left' : 'text-center')}
          visibilityState={{
            name: false,
          }}
          sortingState={[{ id: 'totalScore', desc: true }]}
          emptyMessage={() => (
            <Trans
              i18nKey={
                team.members?.length
                  ? 'dashboard.team.table-all-invisible'
                  : accessLevel === 'admin'
                  ? 'dashboard.team.table-no-members-admin'
                  : 'dashboard.team.table-no-members'
              }
              components={
                accessLevel === 'admin'
                  ? {
                      link: <Link to={`${team.path}/settings#roster`} />,
                      modal: (
                        <a
                          className='text-primary cursor-pointer'
                          onClick={() => setAddingMembers(true)}
                        />
                      ),
                    }
                  : undefined
              }
            />
          )}
        />
      </div>

      {addingMembers && (
        <AddTeamMemberModal open={addingMembers} setOpen={setAddingMembers} />
      )}
    </>
  )
}

export default TeamWishlistTable
